import { useRef, type ReactNode, type RefObject } from "react";
import {
  SchemaModalShell,
  type ModalSize,
} from "../../restaurant-schema/modals/SchemaModalShell";
import { useEscapeToClose } from "../../hooks/useEscapeToClose";
import { useFocusOnOpen } from "../../../../hooks/useFocusOnOpen";

/** Тонът на действието: червено — връща нещо назад (Откажи резервацията,
 * Отмени настаняването); кехлибарено — обикновено потвърждение. */
export type ConfirmTone = "danger" | "primary";

// Същите размери и форма като бутоните на SchemaModalShell. „Назад" е с
// контур, действието — ПЛЪТЕН блок с бял текст, както основният бутон в
// потвърждаващите модали на приложението („Да, продължи" в
// DeleteConfirmModal: #DC2626 → hover #B91C1C, неактивен на 55%). Плътните
// danger/brand токени са еднакви и в тъмната тема — белият текст остава
// четим. Локални тук, за да не се пипа шелът.
// Двата бутона запълват цялата ширина на футъра: `flex-auto` дели свободното
// място поравно ВЪРХУ ширината на текста (не поравно общо), така по-дългият
// етикет не се чупи на два реда.
const BUTTON_BASE =
  "flex h-11 flex-auto cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-[10px] border px-[18px] text-[14px] transition-colors duration-150 focus-visible:outline-none focus-visible:ring-2 disabled:cursor-not-allowed sm:h-[42px]";

const ACTION_TONE: Record<ConfirmTone, string> = {
  danger:
    "border-transparent bg-danger font-bold tracking-[-0.2px] text-white hover:bg-danger-deep active:bg-danger-deep focus-visible:ring-danger focus-visible:ring-offset-2 focus-visible:ring-offset-surface disabled:bg-danger/55",
  primary:
    "border-transparent bg-brand font-bold tracking-[-0.2px] text-white hover:bg-brand-deep active:bg-brand-deep focus-visible:ring-brand focus-visible:ring-offset-2 focus-visible:ring-offset-surface disabled:bg-brand/55",
};

/**
 * Потвърждение в „Резервации" („Да откажем ли резервацията?", „Да отменим ли
 * настаняването?") върху общия шел от references/modals.md (клас Default —
 * в Compact заглавието се чупи на два реда): икон чип + заглавие, текстът в
 * тялото, футър „Назад" / действието на цяла ширина. Фокусът
 * по подразбиране е на „Назад" (Enter не изпълнява действието —
 * `useFocusOnOpen`); Esc = „Назад"
 * с preventDefault (иначе минава и глобалното „назад"). Докато действието
 * тече (`pending`) — спинер на бутона, всичко заключено, Esc/фонът не затварят.
 * Изборът в „Настани" (една сметка / отделна за всяка маса) ползва същия
 * модал: `children` вместо текста (широк, тяло без отстъпи — `size`/`flush`),
 * `confirmDisabled` докато изборът не е пълен, `initialFocusRef` — фокусът
 * на избраната възможност (Enter там потвърждава).
 */
const ReservationConfirmModal = ({
  open,
  tone,
  icon,
  title,
  text,
  children,
  size = "default",
  flush = false,
  confirmLabel,
  backLabel,
  pending,
  confirmDisabled = false,
  initialFocusRef,
  onConfirm,
  onBack,
}: {
  open: boolean;
  tone: ConfirmTone;
  icon: ReactNode;
  title: string;
  text?: string;
  // Тялото вместо `text` (избор с карти).
  children?: ReactNode;
  size?: ModalSize;
  flush?: boolean;
  confirmLabel: string;
  backLabel: string;
  pending: boolean;
  confirmDisabled?: boolean;
  // Къде е фокусът при отваряне (по подразбиране — „Назад").
  initialFocusRef?: RefObject<HTMLElement | null>;
  onConfirm: () => void;
  onBack: () => void;
}) => {
  const back = () => {
    if (!pending) onBack();
  };
  useEscapeToClose(open, back);
  const backRef = useRef<HTMLButtonElement>(null);
  useFocusOnOpen(open, initialFocusRef ?? backRef);

  return (
    <SchemaModalShell
      open={open}
      size={size}
      flush={flush}
      icon={icon}
      title={title}
      onClose={back}
      footer={
        <>
          <button
            ref={backRef}
            type="button"
            disabled={pending}
            onClick={back}
            className={`${BUTTON_BASE} border-shade-3 bg-surface font-semibold text-ink-2 hover:bg-fill focus-visible:ring-brand disabled:opacity-50`}
          >
            {backLabel}
          </button>
          <button
            type="button"
            disabled={pending || confirmDisabled}
            onClick={onConfirm}
            className={`${BUTTON_BASE} ${ACTION_TONE[tone]}`}
          >
            {pending && (
              <span className="h-3.5 w-3.5 animate-spin rounded-full border-2 border-white/40 border-t-white" />
            )}
            {confirmLabel}
          </button>
        </>
      }
    >
      {children ?? <p className="m-0 text-[13.5px] font-medium leading-[1.5] text-body">{text}</p>}
    </SchemaModalShell>
  );
};

export default ReservationConfirmModal;
